import React, { useEffect, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Box, InputBase, Divider, IconButton } from '@mui/material';
import colors from 'src/utils/colors';
import { useContentGenReviewStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores/contentGenReviewStore';
import { getProductImageUrl } from 'src/utils/image';
import { RightChevron } from 'src/components/SvgIcons/SvgIcons';
import { getCurrentContentVersion } from 'src/utils/app';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

// Divider color: #031425 at 15% opacity = #D9DCDE
const DIVIDER_COLOR = '#D9DCDE';

const ReviewProgressHeader: React.FC = () => {
  const { beaconNavigator } = useBeaconNavigator();
  const {
    contentGenItemsToReview,
    getCurrentContentGenItemIndex,
    goToNextContentGenItem,
    goToPreviousContentGenItem,
    setCurrentContentGenItemIndex
  } = useContentGenReviewStore();

  const total = contentGenItemsToReview.length;
  const index = getCurrentContentGenItemIndex();
  const currentItem = contentGenItemsToReview[index];
  const [inputValue, setInputValue] = useState<string>(String(index + 1));

  useEffect(() => {
    setInputValue(String(index + 1));
  }, [index]);

  // Reset state on unmount
  useEffect(() => {
    return () => {
      setCurrentContentGenItemIndex(0);
    };
  }, [setCurrentContentGenItemIndex]);

  if (total <= 0) return null;

  const isFirstItem = index === 0;
  const isLastItem = index === total - 1;

  const commitInput = () => {
    const parsed = parseInt(inputValue, 10);
    if (Number.isNaN(parsed)) {
      setInputValue(String(index + 1));
      return;
    }
    const clamped = Math.max(1, Math.min(total, parsed));
    setCurrentContentGenItemIndex(clamped - 1);
    setInputValue(String(clamped));
  };

  const handleSkuClick = () => {
    if (!currentItem?.stacklineSku) return;

    const version = getCurrentContentVersion();
    const contentTab =
      version === 'content'
        ? BEACON_TABS.CONTENT
        : version === 'contentV2'
        ? BEACON_TABS.CONTENT_V2
        : BEACON_TABS.CONTENT_V3;

    beaconNavigator
      .setPathname(`/product/${currentItem.stacklineSku}`)
      .updateSearchParams({ tab: contentTab, subtab: BEACON_SUBTABS.CONTENT_UTILIZATION })
      .go();
  };

  return (
    <Box
      alignItems="center"
      sx={{
        display: 'flex',
        borderRadius: '8px',
        border: `1px solid ${colors.graysSecondary}`,
        backgroundColor: colors.contentGenBackground,
        height: '56px',
        padding: '0 16px'
      }}
    >
      {currentItem && (
        <Flex alignItems="center" gap={8} sx={{ minWidth: 0 }}>
          <img
            src={getProductImageUrl(currentItem.stacklineSku)}
            alt={currentItem.product?.title || 'Product image'}
            style={{ width: '30px', height: '30px', objectFit: 'cover', borderRadius: '4px' }}
          />
          <Text
            onClick={handleSkuClick}
            sx={{
              color: colors.darkBlue,
              fontWeight: 500,
              fontSize: '12px',
              maxWidth: '240px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: currentItem?.stacklineSku ? 'pointer' : 'default',
              '&:hover': currentItem?.stacklineSku
                ? {
                    textDecoration: 'underline'
                  }
                : undefined
            }}
          >
            {currentItem.retailerSku || '-'}
          </Text>
        </Flex>
      )}
      <Divider
        orientation="vertical"
        sx={{
          mx: '12px',
          borderRadius: '1px',
          borderColor: DIVIDER_COLOR,
          height: '24px',
          alignSelf: 'center'
        }}
      />
      {/* Left chevron - hidden on first item */}
      {!isFirstItem && (
        <IconButton
          onClick={goToPreviousContentGenItem}
          sx={{
            backgroundColor: colors.opaqueGrey,
            borderRadius: '2px',
            width: '24px',
            height: '24px',
            padding: 0,
            mr: '8px',
            '&:hover': {
              backgroundColor: colors.lighterGrey
            }
          }}
          aria-label="Previous item"
        >
          <RightChevron style={{ transform: 'rotate(180deg)', width: '20px', height: '20px' }} />
        </IconButton>
      )}
      <Flex alignItems="center" justifyContent="center">
        <InputBase
          value={inputValue}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9]/g, '');
            setInputValue(v);
          }}
          onBlur={commitInput}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitInput();
            }
          }}
          inputProps={{
            'aria-label': 'current review index',
            style: {
              width: '24px',
              height: '24px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 500,
              color: colors.darkBlue
            }
          }}
          sx={{
            border: `1px solid ${colors.graysSecondary}`,
            borderRadius: '2px',
            height: '24px',
            mr: '4px'
          }}
        />
        <Text sx={{ color: colors.darkBlue, fontWeight: 500, fontSize: '12px' }}>/ {total}</Text>
      </Flex>
      {/* Right chevron - hidden on last item */}
      {!isLastItem && (
        <IconButton
          onClick={goToNextContentGenItem}
          sx={{
            backgroundColor: colors.opaqueGrey,
            borderRadius: '2px',
            width: '24px',
            height: '24px',
            padding: 0,
            ml: '8px',
            '&:hover': {
              backgroundColor: colors.lighterGrey
            }
          }}
          aria-label="Next item"
        >
          <RightChevron style={{ width: '20px', height: '20px' }} />
        </IconButton>
      )}
    </Box>
  );
};

export default ReviewProgressHeader;
